import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Content, Dialog, DialogContainer, Divider, Heading } from '@adobe/react-spectrum';

import { buildSynergyGraph } from '../../../data/buildSynergyGraph';
import { synergyCategoryColor, SYNERGY_CATEGORY_SHORT_LABELS } from '../../../data/synergyCategoryConstants';
import { formatInt, formatPct } from '../../common/cardStats/formatters';
import { synergyEnumLabel } from '../../../data/deckSynergyProfileMarkers';
import '../../common/searedDialog/searedDialog.css';
import './SynergyWebModal.css';

const DEFAULT_VIEW_WIDTH = 1400;
const DEFAULT_VIEW_HEIGHT = 800;
const MOBILE_BREAKPOINT = 620;
const DEFAULT_NODE_LIMIT = 25;
const MIN_NODES = 8;
const MAX_NODES = 40;

const REPEL = 14000;
const SPRING = 0.015;
const LINK_BASE = 170;
const GRAVITY = 0.006;
const DAMPING = 0.84;
const TICKS = 320;

function seedPositions(nodes, width, height) {
    const positions = new Map();
    const cx = width / 2;
    const cy = height / 2;
    const radius = Math.min(width, height) * 0.32;
    const n = Math.max(nodes.length, 1);
    nodes.forEach((node, i) => {
        const angle = (i / n) * Math.PI * 2;
        positions.set(node.id, {
            x: cx + Math.cos(angle) * radius + (Math.random() - 0.5) * 12,
            y: cy + Math.sin(angle) * radius + (Math.random() - 0.5) * 12,
            vx: 0,
            vy: 0,
        });
    });
    return positions;
}

function nodeRadius(score, maxScore) {
    const ratio = Math.sqrt((score || 0) / Math.max(1, maxScore));
    return 6 + ratio * 18;
}

export function SynergyWebModal({ isOpen, onClose, synergyList, cards, profile }) {
    const [nodeLimit, setNodeLimit] = useState(DEFAULT_NODE_LIMIT);
    const [hoveredId, setHoveredId] = useState(null);
    const [animPositions, setAnimPositions] = useState(null);
    const [viewSize, setViewSize] = useState({
        width: DEFAULT_VIEW_WIDTH,
        height: DEFAULT_VIEW_HEIGHT,
    });
    const [isMobile, setIsMobile] = useState(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
        return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches;
    });
    const stageRef = useRef(null);
    const rafRef = useRef();
    const tickIndexRef = useRef(0);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
        const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
        const update = (e) => setIsMobile(e.matches);
        setIsMobile(mql.matches);
        if (mql.addEventListener) mql.addEventListener('change', update);
        else mql.addListener(update);
        return () => {
            if (mql.removeEventListener) mql.removeEventListener('change', update);
            else mql.removeListener(update);
        };
    }, []);

    useEffect(() => {
        if (!isOpen) return undefined;
        const el = stageRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const apply = (w, h) => {
            if (w <= 0 || h <= 0) return;
            setViewSize((prev) =>
                Math.abs(prev.width - w) < 2 && Math.abs(prev.height - h) < 2 ? prev : { width: w, height: h }
            );
        };
        apply(Math.round(el.clientWidth), Math.round(el.clientHeight));
        const ro = new ResizeObserver((entries) => {
            const rect = entries[0].contentRect;
            apply(Math.round(rect.width), Math.round(rect.height));
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [isOpen]);

    const graph = useMemo(() => buildSynergyGraph(synergyList, { nodeLimit }), [synergyList, nodeLimit]);

    // Neighborhood index so hover-dimming is O(1) per element.
    const neighbors = useMemo(() => {
        const map = new Map();
        graph.nodes.forEach((n) => map.set(n.id, new Set([n.id])));
        graph.edges.forEach((e) => {
            map.get(e.source)?.add(e.target);
            map.get(e.target)?.add(e.source);
        });
        return map;
    }, [graph]);

    const hubIds = useMemo(() => {
        if (!Array.isArray(profile?.hubs)) return new Set();
        return new Set(profile.hubs.map((h) => h.cardId));
    }, [profile]);

    const maxScore = useMemo(() => Math.max(1, ...graph.nodes.map((n) => n.score || 0)), [graph]);
    const maxWeight = useMemo(() => Math.max(1, ...graph.edges.map((e) => e.weight || 0)), [graph]);

    // Animated force-directed layout — run the sim in a useEffect so the
    // graph visibly "settles" instead of popping into a finished shape.
    // Cheap enough at n≤40 to run each frame; breaks early when alpha
    // falls below the convergence floor.
    useEffect(() => {
        if (!isOpen || graph.nodes.length === 0) {
            setAnimPositions(null);
            return undefined;
        }
        const { width, height } = viewSize;
        const positions = seedPositions(graph.nodes, width, height);
        let alpha = 1;
        tickIndexRef.current = 0;
        const cx = width / 2;
        const cy = height / 2;
        const nodeIds = graph.nodes.map((n) => n.id);
        const scoreById = new Map(graph.nodes.map((n) => [n.id, n.score || 0]));

        const step = () => {
            // Run a handful of ticks per frame so big graphs still settle
            // quickly without blowing the frame budget on any one render.
            for (let pass = 0; pass < 3; pass++) {
                for (let i = 0; i < nodeIds.length; i++) {
                    const a = positions.get(nodeIds[i]);
                    for (let j = i + 1; j < nodeIds.length; j++) {
                        const b = positions.get(nodeIds[j]);
                        const dx = a.x - b.x;
                        const dy = a.y - b.y;
                        const d2 = dx * dx + dy * dy + 25;
                        const d = Math.sqrt(d2);
                        const f = (REPEL / d2) * alpha;
                        const fx = (dx / d) * f;
                        const fy = (dy / d) * f;
                        a.vx += fx;
                        a.vy += fy;
                        b.vx -= fx;
                        b.vy -= fy;
                    }
                }
                graph.edges.forEach((edge) => {
                    const a = positions.get(edge.source);
                    const b = positions.get(edge.target);
                    if (!a || !b) return;
                    const dx = b.x - a.x;
                    const dy = b.y - a.y;
                    const d = Math.sqrt(dx * dx + dy * dy) + 0.01;
                    const target = LINK_BASE / Math.sqrt(edge.weight);
                    const f = (d - target) * SPRING * alpha;
                    const fx = (dx / d) * f;
                    const fy = (dy / d) * f;
                    a.vx += fx;
                    a.vy += fy;
                    b.vx -= fx;
                    b.vy -= fy;
                });
                positions.forEach((p, id) => {
                    const weight = 0.4 + 0.6 * Math.sqrt((scoreById.get(id) || 0) / maxScore);
                    p.vx += (cx - p.x) * GRAVITY * weight * alpha;
                    p.vy += (cy - p.y) * GRAVITY * weight * alpha;
                    p.x += p.vx;
                    p.y += p.vy;
                    p.vx *= DAMPING;
                    p.vy *= DAMPING;
                    p.x = Math.max(40, Math.min(width - 40, p.x));
                    p.y = Math.max(40, Math.min(height - 40, p.y));
                });
                alpha *= 0.992;
                tickIndexRef.current += 1;
            }

            setAnimPositions(new Map(positions));

            if (tickIndexRef.current < TICKS && alpha > 0.02) {
                rafRef.current = requestAnimationFrame(step);
            }
        };

        rafRef.current = requestAnimationFrame(step);
        return () => cancelAnimationFrame(rafRef.current);
    }, [isOpen, graph, maxScore, viewSize]);

    if (!isOpen) return null;

    const positions = animPositions || seedPositions(graph.nodes, viewSize.width, viewSize.height);
    const hoveredSet = hoveredId ? neighbors.get(hoveredId) || new Set([hoveredId]) : null;
    const shapeLabel = [
        synergyEnumLabel('webTopology', profile?.webTopology),
        synergyEnumLabel('concentration', profile?.concentration),
    ]
        .filter(Boolean)
        .join(' · ');

    return (
        <DialogContainer type="modal" isDismissable onDismiss={onClose}>
            <Dialog
                width="calc(100vw - 32px)"
                height="calc(100vh - 48px)"
                UNSAFE_className="searedDialog synergyWebDialog"
            >
                <Heading>
                    <span className="searedDialog__eyebrow">Synergy web</span>
                    <span className="searedDialog__title">{shapeLabel || 'Synergy dependencies'}</span>
                    <span className="searedDialog__subtitle">
                        Each card is a node — edges connect cards that trigger or enable each other. Size tracks score
                        contribution; color tracks dominant category.
                    </span>
                </Heading>
                <Divider />
                <Content>
                    <div className="synergyWebBody">
                        <div className="synergyWebToolbar">
                            <div className="synergyWebToolbarMetric">
                                <span className="synergyWebToolbarLabel">Top</span>
                                <input
                                    type="range"
                                    min={MIN_NODES}
                                    max={MAX_NODES}
                                    value={nodeLimit}
                                    onChange={(e) => setNodeLimit(parseInt(e.target.value, 10))}
                                    className="synergyWebSlider"
                                    aria-label="Number of nodes to show"
                                />
                                <span className="synergyWebToolbarValue">{nodeLimit}</span>
                                <span className="synergyWebToolbarHint">cards</span>
                            </div>
                            <div className="synergyWebToolbarMetric">
                                <span className="synergyWebToolbarLabel">Showing</span>
                                <span className="synergyWebToolbarValue">{graph.nodes.length}</span>
                                <span className="synergyWebToolbarHint">nodes</span>
                                <span className="synergyWebToolbarSep">·</span>
                                <span className="synergyWebToolbarValue">{graph.edges.length}</span>
                                <span className="synergyWebToolbarHint">edges</span>
                            </div>
                            {profile?.gini != null && (
                                <div className="synergyWebToolbarMetric">
                                    <span className="synergyWebToolbarLabel">Gini</span>
                                    <span className="synergyWebToolbarValue">{profile.gini.toFixed(2)}</span>
                                    {profile.topConditionShare != null && (
                                        <>
                                            <span className="synergyWebToolbarSep">·</span>
                                            <span className="synergyWebToolbarLabel">Top entry</span>
                                            <span className="synergyWebToolbarValue">
                                                {formatPct(profile.topConditionShare)}
                                            </span>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {graph.nodes.length === 0 ? (
                            <div className="synergyWebEmpty">No synergy entries available to graph for this deck.</div>
                        ) : (
                            <>
                                {isMobile && (
                                    <SynergyWebLegend
                                        categories={Array.from(
                                            new Set(graph.nodes.map((n) => n.category).filter(Boolean))
                                        )}
                                        hubCount={graph.nodes.filter((n) => hubIds.has(n.id)).length}
                                        inline
                                    />
                                )}
                                <div className="synergyWebStage" ref={stageRef}>
                                    <svg
                                        viewBox={`0 0 ${viewSize.width} ${viewSize.height}`}
                                        preserveAspectRatio="xMidYMid meet"
                                        className="synergyWebSvg"
                                        onMouseLeave={() => setHoveredId(null)}
                                        onClick={(e) => {
                                            if (e.target === e.currentTarget) setHoveredId(null);
                                        }}
                                        role="img"
                                        aria-label="Synergy dependency graph"
                                    >
                                        <defs>
                                            <radialGradient id="synergyWebBg" cx="50%" cy="50%" r="65%">
                                                <stop offset="0%" stopColor="rgba(255, 60, 60, 0.05)" />
                                                <stop offset="60%" stopColor="rgba(0, 0, 0, 0)" />
                                            </radialGradient>
                                        </defs>
                                        <rect
                                            x="0"
                                            y="0"
                                            width={viewSize.width}
                                            height={viewSize.height}
                                            fill="url(#synergyWebBg)"
                                        />

                                        <g className="synergyWebEdges">
                                            {graph.edges.map((e) => {
                                                const a = positions.get(e.source);
                                                const b = positions.get(e.target);
                                                if (!a || !b) return null;
                                                const inHover =
                                                    !hoveredSet ||
                                                    (hoveredSet.has(e.source) && hoveredSet.has(e.target));
                                                const strokeWidth = 0.8 + (e.weight / maxWeight) * 2.2;
                                                return (
                                                    <line
                                                        key={`${e.source}_${e.target}`}
                                                        x1={a.x}
                                                        y1={a.y}
                                                        x2={b.x}
                                                        y2={b.y}
                                                        strokeWidth={strokeWidth}
                                                        className={`synergyWebEdge${inHover ? '' : ' is-dim'}${
                                                            hoveredSet && inHover ? ' is-active' : ''
                                                        }`}
                                                    />
                                                );
                                            })}
                                        </g>

                                        <g className="synergyWebNodes">
                                            {graph.nodes.map((node) => {
                                                const p = positions.get(node.id);
                                                if (!p) return null;
                                                const card = cards?.[node.id];
                                                const r = nodeRadius(node.score, maxScore);
                                                const color = synergyCategoryColor(node.category);
                                                const isHub = hubIds.has(node.id);
                                                const inHover = !hoveredSet || hoveredSet.has(node.id);
                                                const isFocused = hoveredId === node.id;
                                                const name = card?.name || node.id;
                                                return (
                                                    <g
                                                        key={node.id}
                                                        transform={`translate(${p.x}, ${p.y})`}
                                                        className={`synergyWebNode${inHover ? '' : ' is-dim'}${
                                                            isFocused ? ' is-focus' : ''
                                                        }${isHub ? ' is-hub' : ''}`}
                                                        onMouseEnter={() => setHoveredId(node.id)}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setHoveredId((prev) => (prev === node.id ? null : node.id));
                                                        }}
                                                        aria-label={`${name} — ${formatInt(node.score)} pts`}
                                                    >
                                                        {isHub && (
                                                            <circle
                                                                r={r + 10}
                                                                className="synergyWebHubHalo"
                                                                fill="none"
                                                                stroke={color}
                                                            />
                                                        )}
                                                        <circle r={r} fill={color} className="synergyWebNodeCircle" />
                                                        {(isFocused || r >= 14) && (
                                                            <text
                                                                y={-r - 6}
                                                                className="synergyWebNodeLabel"
                                                                textAnchor="middle"
                                                            >
                                                                {name}
                                                            </text>
                                                        )}
                                                    </g>
                                                );
                                            })}
                                        </g>
                                    </svg>

                                    {!isMobile && (
                                        <SynergyWebLegend
                                            categories={Array.from(
                                                new Set(graph.nodes.map((n) => n.category).filter(Boolean))
                                            )}
                                            hubCount={graph.nodes.filter((n) => hubIds.has(n.id)).length}
                                        />
                                    )}
                                    {hoveredId && (
                                        <SynergyWebHoverCard
                                            node={graph.nodes.find((n) => n.id === hoveredId)}
                                            card={cards?.[hoveredId]}
                                            maxScore={maxScore}
                                            isHub={hubIds.has(hoveredId)}
                                            neighborCount={(neighbors.get(hoveredId)?.size || 1) - 1}
                                        />
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </Content>
            </Dialog>
        </DialogContainer>
    );
}

function SynergyWebLegend({ categories, hubCount, inline }) {
    if (!categories || categories.length === 0) return null;
    return (
        <div className={`synergyWebLegend${inline ? ' synergyWebLegend--inline' : ''}`}>
            {categories.map((cat) => (
                <div key={cat} className="synergyWebLegendItem">
                    <span className="synergyWebLegendSwatch" style={{ background: synergyCategoryColor(cat) }} />
                    <span className="synergyWebLegendLabel">{SYNERGY_CATEGORY_SHORT_LABELS[cat] || cat}</span>
                </div>
            ))}
            {hubCount > 0 && (
                <div className="synergyWebLegendItem">
                    <span className="synergyWebLegendSwatch synergyWebLegendSwatch--hub" />
                    <span className="synergyWebLegendLabel">Hub ({hubCount})</span>
                </div>
            )}
        </div>
    );
}

function SynergyWebHoverCard({ node, card, maxScore, isHub, neighborCount }) {
    if (!node) return null;
    const name = card?.name || node.id;
    const sharePct = Math.round((node.score / Math.max(1, maxScore)) * 100);
    return (
        <div className="synergyWebHoverCard" role="status" aria-live="polite">
            {card?.imageUri && <img src={card.imageUri} alt={name} className="synergyWebHoverImage" />}
            <div className="synergyWebHoverBody">
                <span className="synergyWebHoverName">{name}</span>
                <div className="synergyWebHoverMeta">
                    <span>
                        <strong>{formatInt(node.score)}</strong> pts
                    </span>
                    <span>
                        <strong>{node.entryCount}</strong> entries
                    </span>
                    <span>
                        <strong>{neighborCount}</strong> connections
                    </span>
                    <span>
                        <strong>{sharePct}%</strong> of top
                    </span>
                </div>
                {isHub && <span className="synergyWebHoverHub">Profile hub</span>}
                <span className="synergyWebHoverHint">Click node to open card</span>
            </div>
        </div>
    );
}
